import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Fuse from 'fuse.js';
import {
  ActivityIcon,
  ArrowLeftIcon,
  BrainCircuitIcon,
  CalendarClockIcon,
  CalendarIcon,
  CalendarPlusIcon,
  ClipboardCopyIcon,
  ClipboardListIcon,
  CpuIcon,
  FlagIcon,
  HouseIcon,
  KeyboardIcon,
  LogOutIcon,
  MailIcon,
  MessageSquareIcon,
  MoonIcon,
  NetworkIcon,
  RotateCcwIcon,
  SendIcon,
  Settings2Icon,
  SettingsIcon,
  ShieldIcon,
  SparklesIcon,
  SunIcon,
  SunMoonIcon,
  UsersIcon,
  WorkflowIcon,
  WrenchIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '~/components/ui/command';
import {
  COPILOT_PREFILL_KEY,
  LAST_COPILOT_THREAD_KEY,
} from '~/features/agentic-chat/hooks/use-copilot-prefill-and-tracker';
import { toast } from '~/hooks/use-toast';
import { useAuthToken } from '~/hooks/use-auth-token';
import { isSuperAdminToken } from '~/lib/auth-permissions';
import { useManagedProjects } from '~/hooks/use-managed-projects';
import { usePermissions } from '~/hooks/use-permissions';
import { useTheme } from '~/lib/theme-provider/theme-provider';
import { FEATURE_FLAGS, useFeatureFlag } from '~/lib/feature-flags';
import { useCommandBarStore, type ScheduledTaskDraft } from '~/stores/command-bar.store';

/** Top-level entry shown in the root list of the command bar. */
type CommandPage = 'root' | 'askCopilot';

interface AskCopilotMode {
  id: 'default' | 'cfo' | 'workforce';
  label: string;
  /** Icon for the breadcrumb. */
  icon: React.ComponentType<{ className?: string }>;
  /** Where to send the user after stashing the prompt in sessionStorage. */
  destination: string;
}

const ASK_COPILOT_MODES: AskCopilotMode[] = [
  { id: 'default', label: 'Copilot', icon: BrainCircuitIcon, destination: '/copilot' },
  { id: 'workforce', label: 'Workforce Insights', icon: UsersIcon, destination: '/workforce-insights' },
];

/**
 * Recommended preset scheduled tasks. Mirrors the suggestions in
 * TaskEventDialog but lets the user create them in one click.
 */
const SCHEDULED_TASK_PRESETS: Array<{
  id: string;
  label: string;
  draft: ScheduledTaskDraft;
}> = [
  {
    id: 'preset-weekly-ops',
    label: 'Weekly ops brief (Mondays)',
    draft: {
      title: 'Weekly operations brief',
      prompt:
        'Create a weekly operations brief of active managed workflows, blocked runs, and recommended owner actions.',
      recurrence: 'weekly',
      scope: 'analyst',
    },
  },
  {
    id: 'preset-daily-ontology',
    label: 'Daily ontology summary',
    draft: {
      title: 'Daily ontology summary',
      prompt:
        'Summarize ontology changes from the past 24 hours, highlight new entities/relationships, and flag risky schema edits.',
      recurrence: 'daily',
      scope: 'ontology_network',
    },
  },
  {
    id: 'preset-validation-check',
    label: 'Daily validation readiness',
    draft: {
      title: 'Daily validation readiness check',
      prompt:
        'Review current ontology draft status, list unresolved validation findings, and suggest the top 3 fixes before deployment.',
      recurrence: 'daily',
      scope: 'ontology_network',
    },
  },
];

/**
 * Reads the most recently visited copilot thread id from localStorage.
 * Returns null when the user hasn't opened a thread yet.
 */
function readLastThreadId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(LAST_COPILOT_THREAD_KEY);
  } catch {
    return null;
  }
}

/**
 * Global command bar (Cmd+K / Ctrl+K palette).
 *
 * Mounted once at the app root. Supports a sub-page pattern: selecting
 * "Ask Copilot…" pushes a freeform input page. Selecting "Schedule AI
 * task…" or "Add meeting…" delegates to host dialogs via the store.
 */
export function CommandBar() {
  const { t } = useTranslation();
  const { isAuthenticated, logout } = useAuth0();
  const navigate = useNavigate();
  const { setTheme } = useTheme();

  const isOpen = useCommandBarStore((s) => s.isOpen);
  const open = useCommandBarStore((s) => s.open);
  const close = useCommandBarStore((s) => s.close);
  const openScheduledTaskDialog = useCommandBarStore((s) => s.openScheduledTaskDialog);
  const openMeetingDialog = useCommandBarStore((s) => s.openMeetingDialog);
  const openShortcutsDialog = useCommandBarStore((s) => s.openShortcutsDialog);

  const { isAdmin, isCFO, hasCopilotAccess } = usePermissions();
  const isPeoplev2Enabled = useFeatureFlag(FEATURE_FLAGS.PEOPLE_V2_ENABLED);

  // Platform super admins (SystemZero staff) gain access to admin-only command
  // bar entries that link into the super-admin-gated `/admin/*` section.
  const authToken = useAuthToken();
  const isSuperAdmin = isSuperAdminToken(authToken);

  const copilotModes = useMemo(
    () => ASK_COPILOT_MODES.filter((m) => m.id !== 'workforce' || isPeoplev2Enabled),
    [isPeoplev2Enabled],
  );

  const [page, setPage] = useState<CommandPage>('root');
  const [search, setSearch] = useState('');
  const [askCopilotMode, setAskCopilotMode] = useState<AskCopilotMode>(ASK_COPILOT_MODES[0]);
  const [askCopilotPrompt, setAskCopilotPrompt] = useState('');
  const askCopilotInputRef = useRef<HTMLTextAreaElement | null>(null);

  const { data: projectsData } = useManagedProjects({ isActive: true });
  const projects = projectsData?.items ?? [];

  // Cmd+K listener
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isToggle = (e.metaKey || e.ctrlKey) && e.key === 'k';
      if (isToggle) {
        e.preventDefault();
        if (isOpen) close();
        else open();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, open, close]);

  // Reset internal state when the palette closes
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setPage('root');
      setAskCopilotPrompt('');
      setAskCopilotMode(ASK_COPILOT_MODES[0]);
    }
  }, [isOpen]);

  // Auto-focus the textarea when entering the Ask Copilot sub-page
  useEffect(() => {
    if (page === 'askCopilot') {
      const id = setTimeout(() => askCopilotInputRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [page]);

  const handleSelect = (action: () => void) => () => {
    action();
  };

  const go = (path: string) => {
    navigate(path);
    close();
  };

  const handleAskCopilot = (modeOverride?: AskCopilotMode) => {
    const text = askCopilotPrompt.trim();
    if (text.length === 0) return;
    const mode = modeOverride ?? askCopilotMode;
    try {
      window.sessionStorage.setItem(COPILOT_PREFILL_KEY, text);
    } catch {
      // sessionStorage unavailable — fall back to navigating without prefill.
    }
    navigate(mode.destination);
    close();
  };

  const handleAskFromSearch = (mode?: AskCopilotMode) => {
    const text = search.trim();
    if (text.length === 0) {
      setPage('askCopilot');
      return;
    }
    try {
      window.sessionStorage.setItem(COPILOT_PREFILL_KEY, text);
    } catch {
      // sessionStorage unavailable — fall back without prefill
    }
    navigate((mode ?? ASK_COPILOT_MODES[0]).destination);
    close();
  };

  const handleResumeLastChat = () => {
    const id = readLastThreadId();
    if (id) {
      go(`/copilot/thread/${id}`);
    } else {
      toast({ title: 'No previous chat found', description: 'Start a new chat from the Copilot.' });
      go('/copilot');
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast({ title: 'Page URL copied', variant: 'success' });
    } catch {
      toast({ title: 'Could not copy URL', variant: 'destructive' });
    }
    close();
  };

  const handleSendFeedback = () => {
    const subject = encodeURIComponent('SystemZero feedback');
    const body = encodeURIComponent(`Page: ${window.location.href}\n\nFeedback:\n`);
    window.location.assign(`mailto:support@systemzero.ai?subject=${subject}&body=${body}`);
    close();
  };

  const fuse = useMemo(() => new Fuse(projects, { keys: ['name', 'subtitle', 'slug'], threshold: 0.35 }), [projects]);
  const jumpToProjects = useMemo(() => {
    if (projects.length === 0) return [];
    return search.trim() ? fuse.search(search).map((r) => r.item) : projects.slice(0, 5);
  }, [projects, search, fuse]);

  if (!isAuthenticated) return null;

  // ---------------------------------------------------------------------------
  // Sub-page: Ask Copilot freeform input
  // ---------------------------------------------------------------------------
  if (page === 'askCopilot') {
    return (
      <CommandDialog
        open={isOpen}
        onOpenChange={(next: boolean) => (next ? open() : close())}
        title={t('commandBar.askCopilot.title', 'Ask Copilot')}
        description={t('commandBar.askCopilot.description', 'Type your question for the AI copilot.')}
        className="max-w-2xl"
      >
        <div className="flex items-center gap-2 border-b px-3 py-2 text-xs">
          <button
            type="button"
            onClick={() => setPage('root')}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 rounded px-1.5 py-1"
          >
            <ArrowLeftIcon className="size-3" />
            Back
          </button>
          <span className="text-muted-foreground">/</span>
          <span className="inline-flex items-center gap-1 font-medium">
            <askCopilotMode.icon className="size-3.5" />
            {askCopilotMode.label}
          </span>
        </div>
        <div className="flex flex-col gap-3 p-4">
          <textarea
            ref={askCopilotInputRef}
            value={askCopilotPrompt}
            onChange={(e) => setAskCopilotPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleAskCopilot();
              }
            }}
            placeholder={t(
              'commandBar.askCopilot.placeholder',
              'What do you want to ask? Press Cmd/Ctrl+Enter to send.',
            )}
            rows={4}
            className="border-input bg-background placeholder:text-muted-foreground focus-visible:ring-ring w-full resize-none rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
          />
          <div className="flex items-center justify-between">
            <div className="flex gap-1">
              {copilotModes.map((mode) => {
                const isActive = mode.id === askCopilotMode.id;
                return (
                  <button
                    key={mode.id}
                    type="button"
                    onClick={() => setAskCopilotMode(mode)}
                    className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${
                      isActive
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <mode.icon className="size-3.5" />
                    {mode.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => handleAskCopilot()}
              disabled={askCopilotPrompt.trim().length === 0}
              className="bg-primary text-primary-foreground inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm disabled:opacity-50"
            >
              <SendIcon className="size-3.5" />
              Send
            </button>
          </div>
        </div>
      </CommandDialog>
    );
  }

  // ---------------------------------------------------------------------------
  // Root list
  // ---------------------------------------------------------------------------
  const trimmedSearch = search.trim();
  const showFreeTextAskFallback = trimmedSearch.length >= 2;

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={(next: boolean) => (next ? open() : close())}
      title={t('commandBar.title', 'Command palette')}
      description={t('commandBar.description', 'Search for actions, pages, and more')}
      className="max-w-2xl"
    >
      <Command shouldFilter={true}>
        <CommandInput
          placeholder={t('commandBar.placeholder', 'Type a command, search, or ask AI…')}
          value={search}
          onValueChange={setSearch}
        />
        <CommandList className="max-h-[480px]">
          <CommandEmpty>
            {showFreeTextAskFallback ? (
              <button
                type="button"
                onClick={() => handleAskFromSearch()}
                className="hover:bg-accent w-full rounded px-3 py-2 text-left text-sm"
              >
                <span className="text-muted-foreground">Ask Copilot:</span>{' '}
                <span className="font-medium">&ldquo;{trimmedSearch}&rdquo;</span>
              </button>
            ) : (
              t('commandBar.empty', 'No results found.')
            )}
          </CommandEmpty>

          {/* AI ----------------------------------------------------------- */}
          {hasCopilotAccess && (
            <CommandGroup heading="AI & Chat">
              <CommandItem
                value="ask copilot question prompt ai"
                onSelect={handleSelect(() => setPage('askCopilot'))}
                data-testid="command-bar-item-ask-copilot"
              >
                <SparklesIcon className="size-4" />
                Ask Copilot…
                <CommandShortcut>⌘K → type</CommandShortcut>
              </CommandItem>
              {showFreeTextAskFallback && (
                <CommandItem
                  value={`ask copilot ${trimmedSearch}`}
                  onSelect={handleSelect(() => handleAskFromSearch())}
                >
                  <SparklesIcon className="size-4" />
                  Ask Copilot: &ldquo;{trimmedSearch}&rdquo;
                </CommandItem>
              )}
              <CommandItem value="resume last chat continue thread" onSelect={handleSelect(handleResumeLastChat)}>
                <RotateCcwIcon className="size-4" />
                Resume last chat
              </CommandItem>
              <CommandItem
                value="browse chat history threads search past"
                onSelect={handleSelect(() => go('/copilot'))}
              >
                <MessageSquareIcon className="size-4" />
                Browse chat history
              </CommandItem>
              <CommandItem value="open copilot ai assistant" onSelect={handleSelect(() => go('/copilot'))}>
                <BrainCircuitIcon className="size-4" />
                Open Copilot
              </CommandItem>
              {isPeoplev2Enabled && (
                <CommandItem
                  value="open workforce insights people copilot"
                  onSelect={handleSelect(() => go('/workforce-insights'))}
                >
                  <UsersIcon className="size-4" />
                  Open Workforce Copilot
                </CommandItem>
              )}
            </CommandGroup>
          )}

          <CommandSeparator />

          {/* Schedule AI work --------------------------------------------- */}
          <CommandGroup heading="Schedule AI work">
            <CommandItem
              value="schedule ai task new recurring agent"
              onSelect={handleSelect(() => openScheduledTaskDialog())}
              data-testid="command-bar-item-schedule-ai-task"
            >
              <CalendarClockIcon className="size-4" />
              Schedule AI task…
            </CommandItem>
            {SCHEDULED_TASK_PRESETS.map((preset) => (
              <CommandItem
                key={preset.id}
                value={`${preset.label} ${preset.draft.prompt ?? ''}`}
                onSelect={handleSelect(() => openScheduledTaskDialog(preset.draft))}
                data-testid={`command-bar-item-${preset.id}`}
              >
                <SparklesIcon className="size-4" />
                {preset.label}
              </CommandItem>
            ))}
            <CommandItem value="view scheduled tasks calendar" onSelect={handleSelect(() => go('/settings'))}>
              <CalendarIcon className="size-4" />
              View scheduled tasks
            </CommandItem>
          </CommandGroup>

          <CommandSeparator />

          {/* Calendar ----------------------------------------------------- */}
          <CommandGroup heading="Calendar">
            <CommandItem
              value="add meeting calendar ics outlook google"
              onSelect={handleSelect(() => openMeetingDialog())}
              data-testid="command-bar-item-add-meeting"
            >
              <CalendarPlusIcon className="size-4" />
              Add meeting to calendar…
            </CommandItem>
          </CommandGroup>

          <CommandSeparator />

          {/* Productivity / Meta ----------------------------------------- */}
          <CommandGroup heading="Productivity">
            <CommandItem value="copy current url page link" onSelect={handleSelect(handleCopyUrl)}>
              <ClipboardCopyIcon className="size-4" />
              Copy current page URL
            </CommandItem>
            <CommandItem
              value="keyboard shortcuts cheatsheet help"
              onSelect={handleSelect(() => openShortcutsDialog())}
            >
              <KeyboardIcon className="size-4" />
              Show keyboard shortcuts
              <CommandShortcut>⌘/</CommandShortcut>
            </CommandItem>
            <CommandItem value="send feedback report bug help" onSelect={handleSelect(handleSendFeedback)}>
              <MailIcon className="size-4" />
              Send feedback
            </CommandItem>
            <CommandItem
              value="theme light bright"
              onSelect={handleSelect(() => {
                setTheme('light');
                close();
              })}
            >
              <SunIcon className="size-4" />
              Switch to Light Mode
            </CommandItem>
            <CommandItem
              value="theme dark night"
              onSelect={handleSelect(() => {
                setTheme('dark');
                close();
              })}
            >
              <MoonIcon className="size-4" />
              Switch to Dark Mode
            </CommandItem>
            <CommandItem
              value="theme system auto"
              onSelect={handleSelect(() => {
                setTheme('system');
                close();
              })}
            >
              <SunMoonIcon className="size-4" />
              Use System Theme
            </CommandItem>
          </CommandGroup>

          <CommandSeparator />

          {/* Navigation -------------------------------------------------- */}
          <CommandGroup heading="Go to">
            <CommandItem value="dashboard home overview start" onSelect={handleSelect(() => go('/'))}>
              <HouseIcon className="size-4" />
              Dashboard
            </CommandItem>
            <CommandItem value="activities tasks work programs" onSelect={handleSelect(() => go('/activities'))}>
              <ActivityIcon className="size-4" />
              Activities
            </CommandItem>
            <CommandItem
              value="projects managed agent initiatives"
              onSelect={handleSelect(() => go('/agents/projects'))}
            >
              <FlagIcon className="size-4" />
              Projects
            </CommandItem>
            <CommandItem
              value="action items todos follow-ups checklist"
              onSelect={handleSelect(() => go('/action-items'))}
            >
              <ClipboardListIcon className="size-4" />
              Action Items
            </CommandItem>
            <CommandItem value="daily briefs standup update" onSelect={handleSelect(() => go('/daily-briefs'))}>
              <CalendarIcon className="size-4" />
              Daily Briefs
            </CommandItem>
            <CommandItem
              value="workforce insights people team headcount"
              onSelect={handleSelect(() => go('/workforce-insights'))}
            >
              <UsersIcon className="size-4" />
              Workforce Insights
            </CommandItem>
            <CommandItem value="agent studio all agents" onSelect={handleSelect(() => go('/agents/all'))}>
              <CpuIcon className="size-4" />
              Agent Studio
            </CommandItem>
            <CommandItem
              value="workflows agent automation pipeline"
              onSelect={handleSelect(() => go('/agents/workflows'))}
            >
              <WorkflowIcon className="size-4" />
              Workflows
            </CommandItem>
            <CommandItem value="tools agent mcp plugins" onSelect={handleSelect(() => go('/agents/tools'))}>
              <WrenchIcon className="size-4" />
              Tools
            </CommandItem>
            <CommandItem value="settings preferences profile" onSelect={handleSelect(() => go('/settings'))}>
              <SettingsIcon className="size-4" />
              Settings
              <CommandShortcut>⌘,</CommandShortcut>
            </CommandItem>
            {isCFO && (
              <CommandItem value="cfo finance dashboard" onSelect={handleSelect(() => go('/copilot'))}>
                <Settings2Icon className="size-4" />
                CFO Copilot
              </CommandItem>
            )}
          </CommandGroup>

          {/* Jump to projects -------------------------------------------- */}
          {jumpToProjects.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Jump to project">
                {jumpToProjects.map((project) => (
                  <CommandItem
                    key={project.id}
                    value={`jumpto ${project.name} ${project.slug}`}
                    onSelect={handleSelect(() => go(`/agents/projects/${project.id}`))}
                  >
                    <FlagIcon className="size-4" />
                    {project.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          {/* Admin ------------------------------------------------------- */}
          {(isAdmin() || isSuperAdmin) && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Admin">
                {isSuperAdmin && (
                  <CommandItem value="admin dashboard" onSelect={handleSelect(() => go('/admin'))}>
                    <ShieldIcon className="size-4" />
                    Admin Dashboard
                  </CommandItem>
                )}
                {isSuperAdmin && (
                  <CommandItem value="admin users invite manage" onSelect={handleSelect(() => go('/admin/users'))}>
                    <UsersIcon className="size-4" />
                    Manage Users
                  </CommandItem>
                )}
                {isAdmin() && (
                  <CommandItem
                    value="admin organisation settings company"
                    onSelect={handleSelect(() => go('/organization'))}
                  >
                    <Settings2Icon className="size-4" />
                    Organisation Settings
                  </CommandItem>
                )}
                {isAdmin() && (
                  <CommandItem value="admin knowledge ontology rag" onSelect={handleSelect(() => go('/ontology'))}>
                    <NetworkIcon className="size-4" />
                    Knowledge Base
                  </CommandItem>
                )}
                {isSuperAdmin && (
                  <CommandItem value="admin chat traces audit logs" onSelect={handleSelect(() => go('/admin/traces'))}>
                    <MessageSquareIcon className="size-4" />
                    Chat Traces & Audit
                  </CommandItem>
                )}
              </CommandGroup>
            </>
          )}

          <CommandSeparator />

          {/* Account ----------------------------------------------------- */}
          <CommandGroup heading="Account">
            <CommandItem value="account settings profile" onSelect={handleSelect(() => go('/settings'))}>
              <SettingsIcon className="size-4" />
              Account Settings
            </CommandItem>
            <CommandItem
              value="logout sign out exit quit"
              onSelect={handleSelect(() => {
                logout({ logoutParams: { returnTo: window.location.origin } });
                close();
              })}
            >
              <LogOutIcon className="size-4" />
              Sign Out
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
